/**
 * FloorplanBoundary utility
 * 
 * Provides centralized boundary calculations and clipping utilities
 * to ensure all SVG elements stay within floorplan dimensions.
 */

/**
 * Interface for bounding box dimensions
 */
export interface BoundingBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  width: number;
  height: number;
  cx: number;
  cy: number;
}

/**
 * Interface for floorplan boundaries
 */
export interface FloorplanBoundaries {
  bbox: BoundingBox;
  scale: number;
  zoom: boolean;
  clipRect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  safeRect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

/**
 * Interface for area location
 */
export interface AreaLocation {
  left: number;
  top: number;
  length: number;
  width: number;
}

/**
 * Calculate consistent floorplan boundaries for all components
 * 
 * @param bbox - Drawing bounding box
 * @param scale - Current scale factor
 * @param zoom - Whether zoom mode is active
 * @param padding - Optional padding (default: 2px)
 * @returns FloorplanBoundaries object with consistent coordinate system
 */
export const getFloorplanBoundaries = (
  bbox: BoundingBox,
  scale: number,
  zoom: boolean,
  padding = 2
): FloorplanBoundaries => {
  // Main clipping rectangle (exact floorplan bounds)
  const clipRect = {
    x: bbox.x0,
    y: bbox.y0,
    width: bbox.width,
    height: bbox.height
  };

  // Safe rectangle (slightly inset for device movement, etc.)
  const safeRect = {
    x: bbox.x0 + padding,
    y: bbox.y0 + padding,
    width: bbox.width - (padding * 2),
    height: bbox.height - (padding * 2)
  };

  return {
    bbox,
    scale,
    zoom,
    clipRect,
    safeRect
  };
};

/**
 * Calculate area position within floorplan boundaries
 * 
 * @param area - Area location data
 * @param boundaries - Floorplan boundaries
 * @returns Positioned area coordinates
 */
export const getAreaPosition = (
  area: AreaLocation,
  boundaries: FloorplanBoundaries
) => {
  const { bbox, scale, zoom } = boundaries;
  
  return {
    x: bbox.x0 + (area.left * scale * (!zoom ? 1 : 0)),
    y: bbox.y0 + (area.top * scale * (!zoom ? 1 : 0)),
    width: area.length * scale,
    height: area.width * scale
  };
};

/**
 * Generate SVG clipPath definition for floorplan boundaries
 * 
 * @param boundaries - Floorplan boundaries
 * @param clipId - Unique ID for the clip path
 * @returns SVG clipPath element props
 */
export const generateClipPath = (
  boundaries: FloorplanBoundaries,
  clipId: string
) => {
  const { clipRect } = boundaries;
  
  return {
    id: clipId,
    rect: {
      x: clipRect.x,
      y: clipRect.y,
      width: clipRect.width,
      height: clipRect.height
    }
  };
};

/**
 * Generate SVG clipPath definition for safe area (with padding)
 * 
 * @param boundaries - Floorplan boundaries  
 * @param clipId - Unique ID for the clip path
 * @returns SVG clipPath element props for safe area
 */
export const generateSafeClipPath = (
  boundaries: FloorplanBoundaries,
  clipId: string
) => {
  const { safeRect } = boundaries;
  
  return {
    id: clipId,
    rect: {
      x: safeRect.x,
      y: safeRect.y,
      width: safeRect.width,
      height: safeRect.height
    }
  };
};

/**
 * Check if a point is within floorplan boundaries
 * 
 * @param x - X coordinate
 * @param y - Y coordinate
 * @param boundaries - Floorplan boundaries
 * @param useSafeRect - Whether to use safe rectangle (default: false)
 * @returns True if point is within boundaries
 */
export const isWithinBounds = (
  x: number,
  y: number,
  boundaries: FloorplanBoundaries,
  useSafeRect = false
): boolean => {
  const rect = useSafeRect ? boundaries.safeRect : boundaries.clipRect;
  
  return (
    x >= rect.x &&
    x <= rect.x + rect.width &&
    y >= rect.y &&
    y <= rect.y + rect.height
  );
};

/**
 * Constrain coordinates to stay within boundaries
 * 
 * @param x - X coordinate
 * @param y - Y coordinate
 * @param boundaries - Floorplan boundaries
 * @param useSafeRect - Whether to use safe rectangle (default: false)
 * @returns Constrained coordinates
 */
export const constrainToBounds = (
  x: number,
  y: number,
  boundaries: FloorplanBoundaries,
  useSafeRect = false
): { x: number; y: number } => {
  const rect = useSafeRect ? boundaries.safeRect : boundaries.clipRect;
  
  return {
    x: Math.max(rect.x, Math.min(rect.x + rect.width, x)),
    y: Math.max(rect.y, Math.min(rect.y + rect.height, y))
  };
};

/**
 * Master clip path ID for all floorplan elements
 */
export const MASTER_CLIP_PATH_ID = 'floorplan-master-clip';

/**
 * Safe clip path ID for moveable elements (devices, etc.)
 */
export const SAFE_CLIP_PATH_ID = 'floorplan-safe-clip';
